import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";

import { useManualTime } from "@/api/queries";
import { isStaff, useCurrentUser } from "@/app/auth";
import { cn } from "@/lib/utils";

type Section = "week" | "manual" | "team";

/** Sections of the timesheets area. */
export function TimesheetTabs() {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const staff = isStaff(me);
  const { pathname } = useLocation();
  const pending = usePendingReviews();
  const current: Section =
    pathname === "/timesheets/manual" ? "manual" : pathname === "/timesheets/team" ? "team" : "week";
  const tabs: { key: Section; to: string; count?: number }[] = [
    { key: "week", to: "/timesheets" },
    ...(staff ? [{ key: "team" as const, to: "/timesheets/team" }] : []),
    {
      key: "manual",
      to: staff && pending > 0 ? "/timesheets/manual?view=review" : "/timesheets/manual",
      count: pending,
    },
  ];
  return (
    <nav aria-label={t("nav.timesheets")} className="-mt-2 mb-5 flex gap-1 overflow-x-auto border-b">
      {tabs.map((tab) => (
        <Link
          key={tab.key}
          to={tab.to}
          aria-current={tab.key === current ? "page" : undefined}
          className={cn(
            "-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 font-medium whitespace-nowrap transition-colors",
            tab.key === current
              ? "border-primary text-primary"
              : "border-transparent text-muted hover:text-fg",
          )}
        >
          {t(`timesheets.tabs.${tab.key}`)}
          {tab.count ? (
            <span
              className="rounded-full bg-warning-soft px-1.5 text-xs text-warning tabular-nums"
              aria-label={t("timesheets.pendingCount", { count: tab.count })}
            >
              {tab.count}
            </span>
          ) : null}
        </Link>
      ))}
    </nav>
  );
}

/** Number of manual time requests waiting for the current manager or administrator. */
// eslint-disable-next-line react-refresh/only-export-components
export function usePendingReviews(): number {
  const me = useCurrentUser();
  const staff = isStaff(me);
  const list = useManualTime({ view: "review", status: "pending" }, staff);
  return staff ? (list.data?.length ?? 0) : 0;
}
